import { getLanguageBasedUrl, useDictionary } from 'lib/utils';
import { Text, Image, DateField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import classNames from 'classnames/bind';
import styles from './SeasonDetail.module.scss';
import type { EpisodeDetails, SeasonEpisodesProps } from 'models/podcasts-types';
import ShortDescriptionFieldWrapper from 'components/helpers/ShortDescriptionFieldWrapper';
import Button from 'components/helpers/Button/Button';

const cx = classNames.bind(styles);

// See same implementation in PodcastListing.tsx
const SeasonEpisodes: React.FC<SeasonEpisodesProps> = (props) => {
  const { episodes, episodesFallbackImage, titleHeadingLevel, titleTextStyle, titleOverride, episdoesToShowInitially } =
    props;
  const listingItemHeadingLevelValue = 'h3';
  const viewMoreCount = 5;
  const numberToShowInitially = episdoesToShowInitially ?? 5;
  const buttonMoreId = 'viewMore-button';
  const contentId = 'viewMore-content';

  let episodesTitle = useDictionary('Episodes');
  episodesTitle = titleOverride ? titleOverride : episodesTitle;

  const viewMoreLabel = useDictionary('View More');

  const { sitecoreContext } = useSitecoreContext();

  const [viewMore, setViewMore] = useState(false);
  const [displayCount, setDisplayCount] = useState<number>(numberToShowInitially);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const contentRef = useRef<HTMLUListElement>(null);
  const previousDisplayCountRef = useRef<number>(numberToShowInitially);
  const clearHighlightTimeoutRef = useRef<number | null>(null);

  const handleViewMore = () => {
    setViewMore(true);

    const nextDisplayCount = displayCount + viewMoreCount;
    setDisplayCount(nextDisplayCount < episodes.length ? nextDisplayCount : episodes.length);
  };

  //Display episodes by newest first
  const Episodes = useMemo(() => {
    if (!episodes) return [];

    return [...episodes].sort((a, b) => {
      const dateA = new Date(a.fields.episodeDate?.value ?? 0).getTime();
      const dateB = new Date(b.fields.episodeDate?.value ?? 0).getTime();
      return dateB - dateA;
    });
  }, [episodes]);

  useEffect(() => {
    const previousDisplayCount = previousDisplayCountRef.current;

    if (displayCount > previousDisplayCount && contentRef.current) {
      const startIndex = previousDisplayCount;
      const target = contentRef.current.querySelector<HTMLLIElement>(`li[data-index="${startIndex}"]`);

      if (target) {
        target.focus();
        setHighlightedIndex(startIndex);

        if (clearHighlightTimeoutRef.current !== null) {
          window.clearTimeout(clearHighlightTimeoutRef.current);
        }

        clearHighlightTimeoutRef.current = window.setTimeout(() => {
          setHighlightedIndex(null);
          clearHighlightTimeoutRef.current = null;
        }, 2000);
      }
    }

    previousDisplayCountRef.current = displayCount;
  }, [displayCount]);

  useEffect(() => {
    return () => {
      if (clearHighlightTimeoutRef.current !== null) {
        window.clearTimeout(clearHighlightTimeoutRef.current);
      }
    };
  }, []);

  if (episodes?.length === 0) {
    return <></>;
  }

  return (
    <div className={cx('podcasts-season-episodes')}>
      <Text
        field={{ value: episodesTitle }}
        tag={titleHeadingLevel || 'h2'}
        className={cx({ title: true, [titleTextStyle || 'font-style-xl']: true })}
      />
      <ul id={contentId} ref={contentRef}>
        {Episodes.slice(0, displayCount).map((item: EpisodeDetails, index: number) => (
          <li
            key={index}
            className={cx('listing-card', { 'newly-revealed': highlightedIndex === index })}
            data-index={index}
            tabIndex={-1}
          >
            <div className={cx('listing-image-column')}>
              <Image
                field={item.fields.episodePhoto.value?.src ? item.fields.episodePhoto : episodesFallbackImage}
                className={cx('listing-image-column__image')}
              />
            </div>
            <div className={cx('blog-column')}>
              {!item.fields.episodeDate?.value.startsWith('0001') && (
                <div>
                  <DateField
                    field={item.fields.episodeDate}
                    render={(date) =>
                      date &&
                      date.toLocaleString(sitecoreContext.language, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    }
                  />
                </div>
              )}
              <a href={getLanguageBasedUrl(item.url, sitecoreContext.language)}>
                <Text
                  tag={listingItemHeadingLevelValue}
                  field={item.fields.episodeTitle}
                  className={cx('listing-card__link')}
                />
              </a>

              <ShortDescriptionFieldWrapper textField={item.fields.description} />
            </div>
          </li>
        ))}
      </ul>
      {Episodes.length > displayCount && (
        <Button
          aria-controls={contentId}
          aria-expanded={viewMore}
          className={styles['viewButton']}
          id={buttonMoreId}
          onClick={handleViewMore}
          type="button"
        >
          {viewMoreLabel}
        </Button>
      )}
    </div>
  );
};

export default SeasonEpisodes;
